import { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, NavLink } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  LayoutDashboard, 
  Building2, 
  Heart, 
  ShoppingBag, 
  Users,
  TrendingUp,
  Coins,
  LogOut,
  ChevronRight,
  Activity
} from 'lucide-react';
import { API } from '../App';

// Dashboard Layout Component
function DashboardLayout({ children, auth }) {
  const navigate = useNavigate();
  const { user, logout, token } = auth;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Vue d\'ensemble', end: true },
    { to: '/admin/partnerships', icon: Building2, label: 'Partenariats' },
    { to: '/admin/donations', icon: Heart, label: 'Dons' },
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-stone-200 flex flex-col fixed h-full">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-emerald-800 rounded-xl flex items-center justify-center">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                InvesThune
              </span>
              <p className="text-xs text-emerald-600 font-medium">Admin</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-stone-100">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <span className="text-emerald-800 font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {children}
      </main>
    </div>
  );
}

// Overview Page
function OverviewPage({ auth }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = auth;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const response = await fetch(`${API}/admin/stats`, {
          credentials: 'include',
          headers
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Entreprises',
      value: stats?.partnerships?.companies || 0,
      icon: Building2,
      color: 'emerald',
      trend: '+12%'
    },
    {
      title: 'Associations',
      value: stats?.partnerships?.associations || 0,
      icon: Heart,
      color: 'orange',
      trend: '+8%'
    },
    {
      title: 'E-commerces',
      value: stats?.partnerships?.ecommerce || 0,
      icon: ShoppingBag,
      color: 'slate',
      trend: '+5%'
    },
    {
      title: 'Salariés actifs',
      value: stats?.total_employees || 0,
      icon: Users,
      color: 'emerald',
      trend: '+23%'
    },
    {
      title: 'Total collecté',
      value: `${(stats?.total_donations || 0).toLocaleString('fr-FR')}€`,
      icon: TrendingUp,
      color: 'emerald',
      trend: '+45%'
    },
    {
      title: 'Actions RSE',
      value: `${(stats?.total_rse || 0).toLocaleString('fr-FR')}€`,
      icon: Activity,
      color: 'orange',
      trend: '+18%'
    }
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Tableau de bord Admin
        </h1>
        <p className="text-slate-600">Superviser l'ensemble des activités InvesThune</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card" data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-800 counter">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  stat.color === 'emerald' ? 'bg-emerald-100' :
                  stat.color === 'orange' ? 'bg-orange-100' : 'bg-slate-100'
                }`}>
                  <stat.icon className={`w-6 h-6 ${
                    stat.color === 'emerald' ? 'text-emerald-600' :
                    stat.color === 'orange' ? 'text-orange-600' : 'text-slate-600'
                  }`} />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <span className="text-emerald-600 text-sm font-medium">{stat.trend}</span>
                <span className="text-slate-400 text-sm">ce mois</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card className="bg-white border-stone-200 rounded-2xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Activité récente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{stats?.partnerships?.companies || 0} entreprises partenaires</p>
                  <p className="text-sm text-slate-500">Partenariats actifs</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{stats?.active_campaigns || 0} campagnes actives</p>
                  <p className="text-sm text-slate-500">Collectes en cours</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{stats?.active_promotions || 0} promotions actives</p>
                  <p className="text-sm text-slate-500">Offres en cours</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Partnerships Page
function PartnershipsPage({ auth }) {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = auth;

  useEffect(() => {
    const fetchPartnerships = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const response = await fetch(`${API}/admin/partnerships`, {
          credentials: 'include',
          headers
        });
        if (response.ok) {
          const data = await response.json();
          setPartnerships(data);
        }
      } catch (error) {
        console.error('Failed to fetch partnerships:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPartnerships();
  }, [token]);

  const getTypeLabel = (type) => {
    const labels = {
      company: 'Entreprise',
      association: 'Association',
      ecommerce: 'E-commerce'
    };
    return labels[type] || type;
  };

  const getTypeBadgeClass = (type) => {
    const classes = {
      company: 'badge-success',
      association: 'badge-warning',
      ecommerce: 'badge-info'
    };
    return classes[type] || 'badge-info';
  };

  return (
    <div data-testid="partnerships-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Partenariats
        </h1>
        <p className="text-slate-600">Gestion des partenaires InvesThune</p>
      </div>

      <Card className="bg-white border-stone-200 rounded-2xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : partnerships.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun partenariat pour le moment</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Partenaire</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th>Date d'inscription</th>
                </tr>
              </thead>
              <tbody>
                {partnerships.map((partner) => (
                  <tr key={partner.partnership_id}>
                    <td className="font-medium text-slate-800">{partner.partner_name}</td>
                    <td>
                      <span className={`badge ${getTypeBadgeClass(partner.partner_type)}`}>
                        {getTypeLabel(partner.partner_type)}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-success">
                        {partner.status === 'active' ? 'Actif' : partner.status}
                      </span>
                    </td>
                    <td className="text-slate-500">
                      {new Date(partner.created_at).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Donations Page
function DonationsPage({ auth }) {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = auth;

  useEffect(() => {
    const fetchDonations = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const response = await fetch(`${API}/admin/all-donations`, {
          credentials: 'include',
          headers
        });
        if (response.ok) {
          const data = await response.json();
          setDonations(data);
        }
      } catch (error) {
        console.error('Failed to fetch donations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDonations();
  }, [token]);

  const totalDonations = donations.reduce((sum, d) => sum + d.amount, 0);

  return (
    <div data-testid="donations-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dons & Collectes
        </h1>
        <p className="text-slate-600">Suivi de toutes les transactions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="stat-card">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Total des dons</p>
            <p className="text-3xl font-bold text-emerald-800 counter">
              {totalDonations.toLocaleString('fr-FR')}€
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Nombre de dons</p>
            <p className="text-3xl font-bold text-slate-800 counter">
              {donations.length}
            </p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Don moyen</p>
            <p className="text-3xl font-bold text-orange-600 counter">
              {donations.length > 0 ? (totalDonations / donations.length).toFixed(2) : 0}€
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-stone-200 rounded-2xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : donations.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun don pour le moment</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID Transaction</th>
                  <th>Campagne</th>
                  <th>Type donateur</th>
                  <th>Montant</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {donations.map((donation) => (
                  <tr key={donation.donation_id}>
                    <td className="font-mono text-sm text-slate-600">{donation.donation_id}</td>
                    <td className="text-slate-800">{donation.campaign_id}</td>
                    <td>
                      <span className={`badge ${donation.donor_type === 'company' ? 'badge-success' : 'badge-info'}`}>
                        {donation.donor_type === 'company' ? 'Entreprise' : 'Salarié'}
                      </span>
                    </td>
                    <td className="font-semibold text-emerald-700">{donation.amount.toFixed(2)}€</td>
                    <td className="text-slate-500">
                      {new Date(donation.created_at).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Main Admin Dashboard Component
export default function AdminDashboard({ auth }) {
  return (
    <DashboardLayout auth={auth}>
      <Routes>
        <Route index element={<OverviewPage auth={auth} />} />
        <Route path="partnerships" element={<PartnershipsPage auth={auth} />} />
        <Route path="donations" element={<DonationsPage auth={auth} />} />
      </Routes>
    </DashboardLayout>
  );
}
